import { DimensionType } from '@aon/tws-common-ui-questions/build/lib/models/enum';
import _ from 'lodash';
import { useReducer } from 'react';
import { ISurveyDimension, ISurveyQuestionEditModel } from '../../models';
import { getUniqueDimensionKey } from '../DimensionHelper';

export enum ManageSurveyDimensionModals {
	None,
	ManageDimension,
	DeleteDimension,
	MoveQuestions,
	CopyQuestions,
	RemoveQuestions,
}

interface IManageSurveyDimensionState {
	surveyDimensions: ISurveyDimension[];
	currentModal: ManageSurveyDimensionModals;
	selectedSurveyDimension?: ISurveyDimension;
	selectedSurveyDimensionQuestions: { [key: string]: ISurveyQuestionEditModel[] };
	selectedQuestionsForBulkAction: ISurveyQuestionEditModel[];
	availableDimensionsForBulkAction: ISurveyDimension[];
}

const generateSelectedQuestionsMap = (dimensions: ISurveyDimension[]) => {
	return dimensions.reduce((acc, dimension) => {
		acc[getUniqueDimensionKey(dimension)] = [];
		return acc;
	}, {});
};

const actions = {
	UPDATE_DIMENSIONS: 'UPDATE_DIMENSIONS',
	CREATE_DIMENSION: 'CREATE_DIMENSION',
	EDIT_DIMENSION: 'EDIT_DIMENSION',
	DELETE_DIMENSION: 'DELETE_DIMENSION',
	SET_SELECTED_DIMENSION: 'SET_SELECTED_DIMENSION',
	TOGGLE_QUESTION_SELECTION: 'TOGGLE_QUESTION_SELECTION',
	TOGGLE_ALL_DIMENSION_QUESTIONS_SELECTION: 'TOGGLE_ALL_DIMENSION_QUESTIONS_SELECTION',
	RESET_DIMENSION_QUESTION_SELECTION: 'RESET_DIMENSION_QUESTION_SELECTION',
	MOVE_QUESTIONS: 'MOVE_QUESTIONS',
	COPY_QUESTIONS: 'COPY_QUESTIONS',
	REMOVE_QUESTIONS: 'REMOVE_QUESTIONS',
	CLOSE_MODAL: 'CLOSE_MODAL',
};

const reducer = (state: IManageSurveyDimensionState, action: any) => {
	switch (action.type) {
		case actions.UPDATE_DIMENSIONS:
			return {
				...state,
				surveyDimensions: action.payload,
				selectedSurveyDimensionQuestions: generateSelectedQuestionsMap(action.payload),
			};
		case actions.CREATE_DIMENSION:
			return { ...state, currentModal: ManageSurveyDimensionModals.ManageDimension };
		case actions.EDIT_DIMENSION:
			return { ...state, currentModal: ManageSurveyDimensionModals.ManageDimension, selectedSurveyDimension: action.payload };
		case actions.DELETE_DIMENSION:
			return { ...state, currentModal: ManageSurveyDimensionModals.DeleteDimension, selectedSurveyDimension: action.payload };
		case actions.SET_SELECTED_DIMENSION:
			return { ...state, selectedSurveyDimension: action.payload };
		case actions.TOGGLE_QUESTION_SELECTION: {
			const { question, isSelected } = action.payload;
			const { selectedSurveyDimensionQuestions } = state;
			const dimensionMapKey = getUniqueDimensionKey(action.payload.dimension);
			//INFO: Because of what seems to be a weird but where using RTL and our common checkbox component, we need to account for
			//the click event happening twice and dispatching the action twice. This results in the state the second time the action is
			//dispatched already being updated and the question being added to the selected list twice.
			const currentSelectedQuestionsForDimensionWithoutToggledQuestion = selectedSurveyDimensionQuestions[dimensionMapKey].filter(
				(sq: ISurveyQuestionEditModel) => sq.surveyQuestionId !== question.surveyQuestionId,
			);
			const updatedSelectedQuestions = isSelected
				? [...currentSelectedQuestionsForDimensionWithoutToggledQuestion, question]
				: currentSelectedQuestionsForDimensionWithoutToggledQuestion;
			return {
				...state,
				selectedSurveyDimensionQuestions: {
					...selectedSurveyDimensionQuestions,
					[dimensionMapKey]: updatedSelectedQuestions,
				},
			};
		}
		case actions.TOGGLE_ALL_DIMENSION_QUESTIONS_SELECTION:
			return {
				...state,
				selectedSurveyDimensionQuestions: {
					...state.selectedSurveyDimensionQuestions,
					[getUniqueDimensionKey(action.payload.dimension)]: action.payload.isSelected ? action.payload.questions : [],
				},
			};
		case actions.RESET_DIMENSION_QUESTION_SELECTION:
			return {
				...state,
				selectedSurveyDimensionQuestions: {
					...state.selectedSurveyDimensionQuestions,
					[getUniqueDimensionKey(action.payload)]: [],
				},
			};
		case actions.MOVE_QUESTIONS: {
			const { sourceDimension, questionsToMove } = action.payload;
			let availableDimensionsForQuestionMove = state.surveyDimensions.filter(
				(d) =>
					d.dimensionId &&
					d.dimensionId !== sourceDimension.dimensionId &&
					!d.surveyQuestions?.some((sq) =>
						questionsToMove.some((qtm: ISurveyQuestionEditModel) => qtm.surveyQuestionId === sq.surveyQuestionId),
					),
			);
			const questionReponseOptionGroups = _.uniq(questionsToMove.map((q: ISurveyQuestionEditModel) => q.sourceResponseOptionGroupId));
			if (questionReponseOptionGroups.length > 1 || questionReponseOptionGroups[0] === undefined) {
				availableDimensionsForQuestionMove = availableDimensionsForQuestionMove.filter((d) => !d.isScored);
			} else {
				availableDimensionsForQuestionMove = availableDimensionsForQuestionMove.filter(
					(d) => !d.isScored || d.sourceResponseOptionGroupId === questionReponseOptionGroups[0],
				);
			}
			return {
				...state,
				currentModal: ManageSurveyDimensionModals.MoveQuestions,
				selectedSurveyDimension: sourceDimension,
				selectedQuestionsForBulkAction: questionsToMove,
				availableDimensionsForBulkAction: availableDimensionsForQuestionMove,
			};
		}
		case actions.COPY_QUESTIONS: {
			const { sourceDimension, questionsToCopy } = action.payload;
			const availableDimensionsForQuestionCopy = state.surveyDimensions.filter(
				(d) =>
					d.dimensionId &&
					(d.dimensionType === DimensionType.Index || !d.isScored) &&
					!d.surveyQuestions?.some((sq) =>
						questionsToCopy.some((qtc: ISurveyQuestionEditModel) => qtc.surveyQuestionId === sq.surveyQuestionId),
					),
			);
			return {
				...state,
				currentModal: ManageSurveyDimensionModals.CopyQuestions,
				selectedSurveyDimension: sourceDimension,
				selectedQuestionsForBulkAction: questionsToCopy,
				availableDimensionsForBulkAction: availableDimensionsForQuestionCopy,
			};
		}
		case actions.REMOVE_QUESTIONS: {
			const { sourceDimension, questionsToRemove } = action.payload;
			return {
				...state,
				currentModal: ManageSurveyDimensionModals.RemoveQuestions,
				selectedSurveyDimension: sourceDimension,
				selectedQuestionsForBulkAction: questionsToRemove,
			};
		}
		case actions.CLOSE_MODAL:
			return {
				...state,
				currentModal: ManageSurveyDimensionModals.None,
				selectedSurveyDimension: undefined,
				selectedQuestionsForBulkAction: [],
				availableDimensionsForBulkAction: [],
			};
	}

	return state;
};

const useSurveyDimensionManagement = (surveyDimensions: ISurveyDimension[]) => {
	const selectedQuestionMap = generateSelectedQuestionsMap(surveyDimensions);
	const [state, dispatch] = useReducer(reducer, {
		surveyDimensions,
		currentModal: ManageSurveyDimensionModals.None,
		selectedSurveyDimensionQuestions: selectedQuestionMap,
		selectedQuestionsForBulkAction: [],
		availableDimensionsForBulkAction: [],
	});

	const updateDimensions = (updatedDimensions: ISurveyDimension[]) => {
		dispatch({ type: actions.UPDATE_DIMENSIONS, payload: updatedDimensions });
	};

	const createDimension = () => {
		dispatch({ type: actions.CREATE_DIMENSION });
	};

	const editDimension = (dimensionToEdit: ISurveyDimension) => {
		dispatch({
			type: actions.EDIT_DIMENSION,
			payload: dimensionToEdit,
		});
	};

	const deleteDimension = (dimensionToDelete: ISurveyDimension) => {
		dispatch({
			type: actions.DELETE_DIMENSION,
			payload: dimensionToDelete,
		});
	};

	const setSelectedDimension = (selectedDimension: ISurveyDimension) => {
		dispatch({
			type: actions.SET_SELECTED_DIMENSION,
			payload: selectedDimension,
		});
	};

	const toggleQuestionSelection = (dimension: ISurveyDimension, question: ISurveyQuestionEditModel, isSelected: boolean) => {
		dispatch({
			type: actions.TOGGLE_QUESTION_SELECTION,
			payload: {
				dimension,
				question,
				isSelected,
			},
		});
	};

	const toggleAllQuestionsDimensionSelection = (
		dimension: ISurveyDimension,
		questions: ISurveyQuestionEditModel[],
		isSelected: boolean,
	) => {
		dispatch({
			type: actions.TOGGLE_ALL_DIMENSION_QUESTIONS_SELECTION,
			payload: {
				dimension,
				questions,
				isSelected,
			},
		});
	};

	const resetDimensionQuestionSelection = (dimension: ISurveyDimension) => {
		dispatch({ type: actions.RESET_DIMENSION_QUESTION_SELECTION, payload: dimension });
	};

	const moveQuestions = (sourceDimension: ISurveyDimension, questionsToMove: ISurveyQuestionEditModel[]) => {
		dispatch({
			type: actions.MOVE_QUESTIONS,
			payload: {
				sourceDimension,
				questionsToMove,
			},
		});
	};

	const copyQuestions = (sourceDimension: ISurveyDimension, questionsToCopy: ISurveyQuestionEditModel[]) => {
		dispatch({
			type: actions.COPY_QUESTIONS,
			payload: {
				sourceDimension,
				questionsToCopy,
			},
		});
	};

	const removeQuestions = (sourceDimension: ISurveyDimension, questionsToRemove: ISurveyQuestionEditModel[]) => {
		dispatch({
			type: actions.REMOVE_QUESTIONS,
			payload: {
				sourceDimension,
				questionsToRemove,
			},
		});
	};

	const closeModal = () => {
		dispatch({ type: actions.CLOSE_MODAL });
	};

	return {
		state,
		updateDimensions,
		createDimension,
		editDimension,
		setSelectedDimension,
		deleteDimension,
		toggleQuestionSelection,
		toggleAllQuestionsDimensionSelection,
		resetDimensionQuestionSelection,
		moveQuestions,
		copyQuestions,
		removeQuestions,
		closeModal,
	};
};

export default useSurveyDimensionManagement;
